import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wrench, Download, ArrowRight, CheckCircle2,
  FileSpreadsheet, FileText, Calculator, ClipboardList,
  Zap, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const tools = [
  {
    title: 'Website Audit Checklist',
    description: 'A comprehensive checklist to evaluate your website\'s performance, SEO, and user experience.',
    category: 'Web Development',
    icon: ClipboardList,
    gradient: 'from-blue-500 to-indigo-500',
    status: 'Coming Soon'
  },
  {
    title: 'Project Scope Template',
    description: 'Define project requirements clearly with this professional scope document template.',
    category: 'Project Management',
    icon: FileText,
    gradient: 'from-emerald-500 to-teal-500',
    status: 'Coming Soon'
  },
  {
    title: 'ROI Calculator',
    description: 'Calculate the potential return on investment for your digital marketing and website projects.',
    category: 'Business Planning',
    icon: Calculator,
    gradient: 'from-violet-500 to-purple-500',
    status: 'Coming Soon'
  },
  {
    title: 'Content Planning Spreadsheet',
    description: 'Organize your content strategy with this ready-to-use planning template.',
    category: 'Marketing',
    icon: FileSpreadsheet,
    gradient: 'from-orange-500 to-amber-500',
    status: 'Coming Soon'
  },
  {
    title: 'Client Intake Form',
    description: 'Streamline your client onboarding with this professional intake form template.',
    category: 'Business Operations',
    icon: ClipboardList,
    gradient: 'from-rose-500 to-pink-500',
    status: 'Coming Soon'
  },
  {
    title: 'Meeting Agenda Template',
    description: 'Run more effective meetings with this structured agenda template.',
    category: 'Productivity',
    icon: FileText,
    gradient: 'from-cyan-500 to-blue-500',
    status: 'Coming Soon'
  }
];

const benefits = [
  'Save hours of work with ready-to-use templates',
  'Professional formats trusted by industry experts',
  'Easily customizable for your specific needs',
  'Regularly updated with new tools and templates',
];

const toolsSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Tools & Templates - Fearless IT Resources",
  "description": "Free downloadable tools and templates for businesses. Website checklists, project templates, calculators, and more from Fearless IT.",
  "publisher": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  }
};

const ToolsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="tools-page">
      <SEO 
        title="Tools & Templates - Free Business Resources"
        description="Free downloadable tools and templates for businesses. Website checklists, project scope templates, ROI calculators, and more from Fearless IT."
        keywords="business templates, free tools, website checklist, project scope template, ROI calculator, content planning template, business resources"
        url="/resources/tools"
        structuredData={toolsSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
            <Wrench className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Resources</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="tools-title">
            Tools & Templates
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #D97706, #F59E0B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Ready-to-Use Resources for Your Business
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            Free, professional templates and tools to help you work smarter and grow your business faster.
          </p>
        </div>
      </section>

      {/* Coming Soon Notice */}
      <section className="py-8 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white font-semibold">
            Our tools library is being built! Subscribe to be notified when templates are available for download.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-6">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-slate-gray">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Available Tools
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Professional templates created to save you time and improve your workflow.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, idx) => {
              const IconComponent = tool.icon;
              return (
                <div key={idx} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden group hover:shadow-xl transition-shadow">
                  <div className={`h-20 bg-gradient-to-r ${tool.gradient} flex items-center justify-center`}>
                    <IconComponent className="h-8 w-8 text-white opacity-90" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-amber-600 uppercase tracking-wider">
                        {tool.category}
                      </span>
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                        {tool.status}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-carbon-black mb-2 group-hover:text-amber-600 transition-colors">
                      {tool.title}
                    </h3>
                    <p className="text-sm text-slate-gray leading-relaxed">
                      {tool.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Free Growth Audit CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 sm:p-12 border border-amber-100">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <Zap className="h-10 w-10 text-amber-500 mb-4" />
                <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
                  Get Your Free Growth Audit
                </h2>
                <p className="text-slate-gray leading-relaxed mb-6">
                  While you wait for our templates, get a personalized assessment of your business's digital presence. Our Growth Audit analyzes your website, SEO, and online presence with actionable recommendations.
                </p>
                <Link to="/growth-audit">
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-6 text-lg font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all">
                    Start Free Audit
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <h3 className="font-bold text-carbon-black mb-4">What's Included:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-gray">Website performance analysis</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-gray">SEO opportunity assessment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-gray">Competitor comparison</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-gray">Actionable recommendations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Request Template CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Need a Specific Template?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Have a tool or template idea? Let us know and we'll consider adding it to our library.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Request a Template
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-amber-400 text-amber-600 hover:bg-amber-50 transition-all w-full sm:w-auto">
                Browse Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ToolsPage;
